import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { updateUsersMap } from "../../globalStore/slices/organization/organizationSlice";
import { updateUserTimezone } from "../../globalStore/slices/user/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { useTranslation as UseTranslation } from "react-i18next";
import AutocompleteSelect from "../../components/Forms/FieldTypes/AutocompleteSelect";
import MaterialConfirmationModal from "../../components/Modals/MaterialConfirmationModal";
import MaterialUiButton from "../../components/Buttons/MaterialUiButton/MaterialUiButton";
import moment from "moment";
import SimpleCheckbox from "../../components/Forms/FieldTypes/Checkbox";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import ScheduleIcon from "@mui/icons-material/Schedule";

function UserProfile(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { typography = {}, primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";
  const greenColor = success?.main || "#A0C46D";
  const navyColor = primary?.main || "#002843";

  const classes = {
    profileDetail: {
      color: isDark ? greenColor : navyColor,
      fontFamily: '"Lato", sans-serif',
      fontSize: "0.85rem",
      fontWeight: 700,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      marginTop: "12px",
    },
    profileValue: {
      fontFamily: '"Lato", sans-serif',
      fontSize: "1rem",
      fontWeight: 400,
      color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.8)",
      marginBottom: "8px",
      paddingLeft: "2px",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "16px",
      marginTop: "8px",
      paddingBottom: "10px",
      borderBottom: `2px solid ${isDark ? greenColor + "30" : navyColor + "15"}`,
    },
    sectionTitle: {
      fontFamily: '"Lato", sans-serif',
      fontWeight: 700,
      fontSize: "1rem",
      color: isDark ? greenColor : navyColor,
      letterSpacing: "-0.01em",
    },
    sectionIcon: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      background: isDark 
        ? `linear-gradient(135deg, ${greenColor}20 0%, ${greenColor}10 100%)`
        : `linear-gradient(135deg, ${navyColor}15 0%, ${greenColor}10 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? greenColor : navyColor,
    },
    infoCard: {
      padding: "16px",
      borderRadius: "12px",
      background: isDark 
        ? "rgba(255,255,255,0.03)"
        : "rgba(0,0,0,0.02)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
      marginBottom: "16px",
    },
  };

  const { apiUrl, token } = props;

  const { t } = UseTranslation();


  const user = useSelector((state) => state.user);
  const dispatchGlobal = useDispatch();

  const {
    appUserId,
    email,
    firstName,
    lastName,
    usersConsoleRoles,
    userPropertiesMap,
    userTimeZone,
    userTimeZoneUseDefault,
  } = user;

  const timeZones = moment.tz.names();
  const browserTimeZone = moment.tz.guess();
  const usersSetTimeZone = userTimeZone ? userTimeZone : browserTimeZone;
  const timeZoneOptions =
    timeZones && timeZones.length
      ? timeZones.map((tz) => {
        return {
          label: tz,
          value: tz,
        };
      })
      : [];

  const phoneNumber = userPropertiesMap?.propertiesMap?.phone;

  const [state, setState] = useState({
    timeZone: {
      error: false,
      id: "timezone",
      value: { label: usersSetTimeZone, value: usersSetTimeZone },
    },
    userTimeZoneUseDefault: {
      id: "userTimeZoneUseDefault",
      value: userTimeZoneUseDefault,
    },
  });

  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "success",
  });

  const { timeZone } = state;
  const fetchUrl = `${apiUrl}appUsers/${appUserId}`;

  function handleSaveTimeZone() {
    // Save the timezone to the user object in the database.
    fetch(fetchUrl, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "auth-token": token,
      },
      body: JSON.stringify({
        userTimeZone: timeZone.value.value,
        userTimeZoneUseDefault: state.userTimeZoneUseDefault.value,
      }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.success) {
          //update user in redux store
          dispatchGlobal(
            updateUserTimezone({
              timeZone: response.appUser?.userTimeZone,
              userTimeZoneUseDefault: response.appUser?.userTimeZoneUseDefault,
            })
          );
          dispatchGlobal(
            updateUsersMap({
              appUserId: response.appUser?.appUserId,
              body: response.appUser,
            })
          );
          //set notification modal state
          setConfirmationModal((prevState) => ({
            ...prevState,
            open: true,
            message: "Timezone saved successfully",
            type: "success",
          }));
        } else {
          setConfirmationModal((prevState) => ({
            ...prevState,
            open: true,
            message: `Error: ${response.error}`,
            type: "error",
          }));
        }
      });
  }

  return (
    (<Grid
      container
      spacing={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        padding: "1.5rem",
      }}
    >
      {/* confirmation modal */}
      <MaterialConfirmationModal
        closeModal={() => {
          setConfirmationModal((prevState) => ({
            ...prevState,
            open: false,
          }));
        }}
        content={confirmationModal.message}
        modalOpen={confirmationModal.open}
        severity={confirmationModal.type || "error"}
      />

      {/* Section: Personal Information */}
      <Box sx={classes.sectionHeader}>
        <Box sx={classes.sectionIcon}>
          <PersonIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Typography sx={classes.sectionTitle}>
          Personal Information
        </Typography>
      </Box>

      <Box sx={classes.infoCard}>
        {/* First Name */}
        <Typography sx={classes.profileDetail}>First Name</Typography>
        <Typography sx={classes.profileValue}>
          {firstName || t("dnpinput1")}
        </Typography>
        {/* Last Name */}
        <Typography sx={classes.profileDetail}>Last Name</Typography>
        <Typography sx={classes.profileValue}>
          {lastName || t("dnpinput1")}
        </Typography>
        {/* Email */}
        <Typography sx={classes.profileDetail}>Email</Typography>
        <Typography sx={classes.profileValue}>
          {email || t("dnpinput1")}
        </Typography>
        {/* Phone Number */}
        <Typography sx={classes.profileDetail}>Phone Number</Typography>
        <Typography sx={classes.profileValue}>
          {phoneNumber || t("dnpinput1")}
        </Typography>
      </Box>

      {/* Section: Role */}
      <Box sx={classes.sectionHeader}>
        <Box sx={classes.sectionIcon}>
          <BadgeIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Typography sx={classes.sectionTitle}>
          Role & Permissions
        </Typography>
      </Box>

      <Box sx={classes.infoCard}>
        <Typography sx={classes.profileDetail}>Role</Typography>
        <Typography sx={classes.profileValue}>
          {usersConsoleRoles[0]?.name || t("dnpinput1")}
        </Typography>
      </Box>

      {/* Section: Time Zone */}
      <Box sx={classes.sectionHeader}>
        <Box sx={classes.sectionIcon}>
          <ScheduleIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Typography sx={classes.sectionTitle}>
          Time Zone Settings
        </Typography>
      </Box>

      <Box sx={classes.infoCard}>
        <Grid sx={{ display: "flex", flexDirection: "column" }} size={12}>
          {/* Time Zone */}
          <AutocompleteSelect
            disabled={state.userTimeZoneUseDefault.value}
            label="Time Zone"
            noOptionsText="Start typing to search assemblies"
            getOptionLabel={(option) => option?.value}
            onChange={(_, value) =>
              setState((prevState) => ({
                ...prevState,
                timeZone: { ...prevState.timeZone, value },
              }))
            }
            options={timeZoneOptions}
            value={timeZone.value}
            variant="outlined"
          />

          {/* Use Browser Timezone */}
          <SimpleCheckbox
            checked={state.userTimeZoneUseDefault.value}
            label="Use Browser Timezone"
            onChange={(event) => {
              setState((prevState) => ({
                ...prevState,
                // We'll pop the timezone back to the default state.
                timeZone: {
                  ...prevState.timeZone,
                  value: { label: browserTimeZone, value: browserTimeZone },
                },
                userTimeZoneUseDefault: {
                  ...prevState.userTimeZoneUseDefault,
                  value: event.target.checked,
                },
              }));
            }}
          />

          <MaterialUiButton
            color="primary"
            fullWidth={false}
            label="Save Time Zone"
            onClick={handleSaveTimeZone}
            variant="contained"
          />
        </Grid>
      </Box>
    </Grid>)
  );
}

export default UserProfile;
